import Modal from './modal'
import Button from '../button'
import { AddressDTO, AddressDTOGenderEnum, MAX_FILE_SIZE_BYTES, MasterDataDTO } from '@ringo/shared'
import { SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { useUser } from '../../../common/hooks/auth/useUser'
import { useTranslation } from '../../../useTranslation'
import ControlledDropdown from '../../form/controlled-dropdown'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import ControlledInputText from '../../form/controlled-input-text'
import { object } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { Country } from '@ringo/shared'
import InputFileSelect from '../input-file'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Validation } from '../../../common/types'
import { useAddressApi, useMasterDataApi } from '../../../common/api-context'
import { useValidationRules } from '../../../common/validation-rules'

interface ModalProps {
    open: boolean
    address?: MasterDataDTO
    onClose: () => void
}

const UpdateAddressModal: React.FC<ModalProps> = ({ open, address, onClose }) => {
    const validationRules = useValidationRules()
    const queryClient = useQueryClient()

    const validationObject: Validation<MasterDataDTO> = {
        gender: validationRules.genders,
        firstName: validationRules.firstName,
        lastName: validationRules.lastName,
        companyName: validationRules.companyName,
        street: validationRules.street,
        houseNumber: validationRules.houseNumber,
        postalCode: validationRules.postalCode,
        city: validationRules.city,
        country: validationRules.country,
        telephone: validationRules.telephone,
    }

    const schema = object(validationObject)

    const { handleSubmit, control, reset } = useForm<MasterDataDTO>({
        resolver: yupResolver(schema as any),
        defaultValues: address
            ? { ...address }
            : {
                  gender: AddressDTOGenderEnum.Male,
                  country: Country.Germany,
              },
    })

    const [selectedDocument, setSelectedDocument] = useState<File | null>(null)
    const [documentUploadError, setDocumentUploadError] = useState<string>()
    const addressApi = useAddressApi()
    const masterDataApi = useMasterDataApi()
    const { refreshUserData } = useUser()
    const { t } = useTranslation()

    const gendersList = useMemo(
        () =>
            Object.entries(AddressDTOGenderEnum).map(([label, id]) => ({
                id: id,
                label: t(label as any),
            })),
        [t]
    )
    const countriesList = useMemo(
        () =>
            Object.entries(Country).map(([label, id]) => ({
                id: id,
                label: t(label as any),
            })),
        [t]
    )

    const createAddress = useMutation({
        mutationFn: (formData: AddressDTO) => {
            return addressApi.createNewAddress(formData as AddressDTO)
        },
        onSuccess: () => {
            refreshUserData()
            reset() // Reset form
            toast.success(t('myAccount.successNewAddressCreated'))
            onClose()
        },
        onError: () => {
            toast.error(t('errorNewRequest'))
        },
    })

    const updateAddress = useMutation({
        mutationFn: async (formData: MasterDataDTO) => {
            const response = await masterDataApi.createMasterDataApproval(selectedDocument!, JSON.stringify(formData))
            queryClient.invalidateQueries({ queryKey: ['getAllMasterData'] })
            return response
        },
        onSuccess: () => {
            refreshUserData()
            toast.success(t('myAccount.addresses.successMasterAddressRequestChange'))
            onClose()
        },
        onError: () => {
            toast.error(t('errorNewRequest'))
        },
    })

    const onSubmit: SubmitHandler<MasterDataDTO> = async (data) => {
        if (address) {
            if (selectedDocument) {
                updateAddress.mutate(data)
            } else {
                setDocumentUploadError('myAccount.Addresses.fileRequired')
            }
        } else {
            createAddress.mutate(data as AddressDTO)
        }
    }

    const handleFileChange = (file: File | null) => {
        if (file && file.size > MAX_FILE_SIZE_BYTES) {
            toast.error(t('myAccount.legitimation.maximumFileSizeIs5MB'))
            file = null
        }

        setSelectedDocument(file)
        setDocumentUploadError('')
    }

    return (
        <Modal title={address ? t('myAccount.changeMasterAddress') : t('myAccount.addresses.createNewLivingAddress')} open={open} onClose={onClose}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="mt-3">
                    <ControlledDropdown options={gendersList} name="gender" control={control} required label={t('gender')} />
                </div>
                <div className="flex gap-[6px] justify-between mt-2">
                    <div className="w-1/2">
                        <ControlledInputText label={t('firstName')} name="firstName" control={control} required />
                    </div>
                    <div className="w-1/2">
                        <ControlledInputText label={t('lastName')} name="lastName" control={control} required />
                    </div>
                </div>
                <div className="mt-2">
                    <ControlledInputText label={t('company')} name="companyName" control={control} required />
                </div>
                <div className="flex gap-[6px] mt-2">
                    <div className="w-2/3">
                        <ControlledInputText label={t('street')} name="street" control={control} required />
                    </div>
                    <div className="w-1/3">
                        <ControlledInputText label={t('houseNumber')} name="houseNumber" control={control} required />
                    </div>
                </div>
                <div className="flex gap-[6px] mt-2">
                    <div className="w-1/3">
                        <ControlledInputText label={t('postalCode')} name="postalCode" control={control} required />
                    </div>
                    <div className="w-2/3">
                        <ControlledInputText label={t('city')} name="city" control={control} required />
                    </div>
                </div>
                <div className="mt-3">
                    <ControlledDropdown options={countriesList} name="country" control={control} label={t('country')} required />
                </div>
                <div className="mt-2">
                    <ControlledInputText label={t('telephone')} name="telephone" control={control} required />
                </div>

                {address && (
                    <div className="mt-4 w-1/3">
                        <InputFileSelect
                            label={t('myAccount.addresses.uploadProof')}
                            required
                            value={selectedDocument}
                            onChange={(file) => {
                                handleFileChange(file)
                            }}
                            error={{
                                key: documentUploadError ?? '',
                                values: {},
                            }}
                        />
                    </div>
                )}

                <p className="text-custom_yellow mt-6 text-base max-tablet:text-sm max-tablet:mt-5">* {t('mandatoryField')}</p>

                {address && (
                    <div className="flex justify-start gap-4 pt-6 -mb-3">
                        <FontAwesomeIcon icon={['fas', 'circle-info']} className="text-custom_blue" />
                        <span className="text-sm">{t('myAccount.addresses.updateAddressDocumentInfo')}</span>
                    </div>
                )}

                <div className="mt-8 mx-auto text-center max-tablet:mt-7">
                    <Button
                        icon={'gear'}
                        text={address ? t('myAccount.addresses.requestChange') : t('myAccount.addresses.addNewDeliveryAddress')}
                        type={'squareBlue'}
                        isLoading={createAddress.isPending || updateAddress.isPending}
                    />
                </div>
            </form>
        </Modal>
    )
}

export default UpdateAddressModal
